import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AdminAuthProvider } from './hooks/useAdminAuth';
import { ClassicAdminLayout } from './components/layout/ClassicAdminLayout';
import { EnterpriseAdminLayout } from './components/layout/EnterpriseAdminLayout';
import { LoginPage } from './pages/auth/LoginPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { UsersPage } from './pages/users/UsersPage';
import { UserDetailPage } from './pages/users/UserDetailPage';
import { WorkspacesPage } from './pages/workspaces/WorkspacesPage';
import { WorkflowsPage } from './pages/workflows/WorkflowsPage';
import { WorkflowHealthPage } from './pages/workflows/WorkflowHealthPage';
import { IntegrationsPage } from './pages/integrations/IntegrationsPage';
import { SystemPage } from './pages/system/SystemPage';
import { MonitoringPage } from './pages/monitoring/MonitoringPage';
import { FastAPIMonitoringPage } from './pages/monitoring/FastAPIMonitoringPage';
import { CopilotMetricsPage } from './pages/monitoring/CopilotMetricsPage';
import { SupabaseMonitorPage } from './pages/monitoring/SupabaseMonitorPage';
import { InvoicesPage } from './pages/invoices/InvoicesPage';
import { PricingPage } from './pages/pricing/PricingPage';
import { PhoneNumbersPage } from './pages/phone-numbers/PhoneNumbersPage';
import { CallingProvidersPage } from './pages/calling-providers/CallingProvidersPage';
import { ApiKeysPage } from './pages/api-keys/ApiKeysPage';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { EnterpriseHomePage } from './pages/enterprise/EnterpriseHomePage';
import { EnterpriseWorkspacesPage } from './pages/enterprise/EnterpriseWorkspacesPage';
import { EnterpriseWorkspaceDetailPage } from './pages/enterprise/EnterpriseWorkspaceDetailPage';
import { EnterpriseVerifiedDomainsPage } from './pages/enterprise/EnterpriseVerifiedDomainsPage';
import { EnterpriseAuditPage } from './pages/enterprise/EnterpriseAuditPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { InvoiceTemplatesPage } from './pages/settings/InvoiceTemplatesPage';
import { JarvisToolsPage } from './pages/jarvis/JarvisToolsPage';

/**
 * `/admin` — auth + outlet only (no layout).
 * Children: index redirect, classic layout branch, enterprise `/admin/app` branch.
 */
function AdminRootOutlet() {
  return <Outlet />;
}

function App() {
  return (
    <AdminAuthProvider>
      <Router>
        <Routes>
          <Route path="/admin/login" element={<LoginPage />} />

          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminRootOutlet />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/admin/dashboard" replace />} />

            {/* Classic layer — original flat nav + pages; behavior unchanged */}
            <Route element={<ClassicAdminLayout />}>
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="users/:userId" element={<UserDetailPage />} />
              <Route path="workspaces" element={<WorkspacesPage />} />
              <Route path="pricing" element={<PricingPage />} />
              <Route path="invoices" element={<InvoicesPage />} />
              <Route path="phone-numbers" element={<PhoneNumbersPage />} />
              <Route path="calling-providers" element={<CallingProvidersPage />} />
              <Route path="api-keys" element={<ApiKeysPage />} />
              <Route path="workflows" element={<WorkflowsPage />} />
              <Route path="workflow-health" element={<WorkflowHealthPage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="settings/invoice-templates" element={<InvoiceTemplatesPage />} />
              <Route path="system" element={<SystemPage />} />
              <Route path="monitoring" element={<MonitoringPage />} />
              <Route path="fastapi-monitoring" element={<FastAPIMonitoringPage />} />
              <Route path="copilot-metrics" element={<CopilotMetricsPage />} />
              <Route path="jarvis-tools" element={<JarvisToolsPage />} />
              <Route path="supabase-monitor" element={<SupabaseMonitorPage />} />
            </Route>

            {/* Enterprise layer — new shell; add future routes as children of this path */}
            <Route path="app" element={<EnterpriseAdminLayout />}>
              <Route index element={<EnterpriseHomePage />} />
              <Route path="verified-domains" element={<EnterpriseVerifiedDomainsPage />} />
              <Route path="audit" element={<EnterpriseAuditPage />} />
              {/* More specific workspace routes first */}
              <Route path="workspaces/table" element={<WorkspacesPage />} />
              <Route path="workspaces/:workspaceId" element={<EnterpriseWorkspaceDetailPage />} />
              <Route path="workspaces" element={<EnterpriseWorkspacesPage />} />
              {/* Mirrored classic pages — same components, enterprise shell */}
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="users/:userId" element={<UserDetailPage />} />
              <Route path="pricing" element={<PricingPage />} />
              <Route path="invoices" element={<InvoicesPage />} />
              <Route path="phone-numbers" element={<PhoneNumbersPage />} />
              <Route path="calling-providers" element={<CallingProvidersPage />} />
              <Route path="api-keys" element={<ApiKeysPage />} />
              <Route path="workflows" element={<WorkflowsPage />} />
              <Route path="workflow-health" element={<WorkflowHealthPage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="settings/invoice-templates" element={<InvoiceTemplatesPage />} />
              <Route path="system" element={<SystemPage />} />
              <Route path="monitoring" element={<MonitoringPage />} />
              <Route path="fastapi-monitoring" element={<FastAPIMonitoringPage />} />
              <Route path="copilot-metrics" element={<CopilotMetricsPage />} />
              <Route path="jarvis-tools" element={<JarvisToolsPage />} />
              <Route path="supabase-monitor" element={<SupabaseMonitorPage />} />
            </Route>
          </Route>

          <Route path="/" element={<Navigate to="/admin/login" replace />} />
          <Route path="*" element={<Navigate to="/admin/login" replace />} />
        </Routes>
      </Router>
    </AdminAuthProvider>
  );
}

export default App;
